import {
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Base, BorderIcon, Container, InputSearchStyle, Label, List, ListContainer, ListItem,
} from './styles';
import { useOuterClickNotifier } from '~/hooks';
import * as IconNames from 'icons';
import { colors } from '~/styles/colors';

type IconName = keyof typeof IconNames;
export type Option = {
  label: string;
  value: string;
  iconName?: IconName;
  comingSoon?: boolean
};

interface BaseSelectProps {
  icon: IconName;
  options: Option[];
  placeholder: string;
  disabled?: boolean;
  style?: CSSProperties;
  onSelect: (value: string) => void;
  dataTestId?: string;
}

interface SingleSelectProps extends BaseSelectProps {
  multiple?: false;
  value?: string;
  isSearchable?: boolean;
}

interface MultiSelectProps extends BaseSelectProps {
  multiple: true;
  value?: string[];
  isSearchable?: boolean;
}

type SelectProps = SingleSelectProps | MultiSelectProps;

export function Select({
  disabled,
  icon,
  options,
  placeholder,
  value,
  onSelect,
  style,
  multiple,
  isSearchable,
  dataTestId,
}: Readonly<SelectProps>): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const clickOutsideCallback = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);
  useOuterClickNotifier(clickOutsideCallback, containerRef);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filteredOptions = useMemo(() => {
    if (!search) return options;

    return options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  function toggleOptions(): void {
    if (disabled) return;
    setIsOpen(!isOpen);
  }

  function onClickItem(itemValue: string, comingSoon?: boolean): void {
    if (comingSoon) return;
    setIsOpen(false);
    setSearch('');
    onSelect(itemValue);
  }

  function renderIcon(icon: string, border?: true): ReactElement | null {
    const Icon = IconNames[icon];
    const iconElement = (
      <Icon
        color={border ? colors.icon['strong-900'] : colors.icon['primary-base']}
        size="14"
      />
    );

    if (border) {
      return <BorderIcon>{iconElement}</BorderIcon>;
    }

    return iconElement;
  }

  function renderLabel(): string {
    if (!value) {
      return placeholder;
    }

    if (multiple) {
      const selectedOptions = options.filter((item) => value.includes(item.value));

      if (selectedOptions.length === 0) {
        return placeholder;
      }

      if (selectedOptions.length > 1) {
        return `${selectedOptions.length} selecionados`;
      }

      return selectedOptions[0].label;
    }

    return options.find((item) => item.value === value)?.label ?? placeholder;
  }

  function isSelectedOption(optionValue: string): boolean {
    if (multiple) {
      return (value ?? []).includes(optionValue);
    }

    return value === optionValue;
  }

  const definedValue = useMemo(
    () => (multiple ? !!value && value.length > 0 : !!value),
    [multiple, value],
  );

  useEffect(() => {
    if (isOpen && isSearchable) {
      inputRef.current?.focus();
    }
  }, [isOpen, isSearchable]);

  return (
    <Container ref={containerRef} style={style} data-testid={dataTestId}>
      <Base selected={definedValue} onClick={toggleOptions} open={isOpen}>
        {renderIcon(icon)}
        <Label selected={definedValue}>{renderLabel()}</Label>
        <IconNames.ChevronRight
          rotate={isOpen ? '270deg' : '90deg'}
          color={
            definedValue ? colors.icon['strong-900'] : colors.icon['soft-400']
          }
          size="14"
        />
      </Base>
      {isOpen && (
        <ListContainer>
          {isSearchable && (
            <div style={{ padding: '8px' }}>
              <InputSearchStyle
                ref={inputRef}
                type="text"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <List>
            {filteredOptions.map((option) => {
              const isSelected = isSelectedOption(option.value);

              return (
                <ListItem
                  key={`option-${option.value.trim().toLowerCase()}`}
                  selected={isSelected}
                  comingSoon={option.comingSoon}
                  onClick={() => onClickItem(option.value, option.comingSoon)}
                >
                  {option.iconName && renderIcon(option.iconName, true)}
                  <span>{option.label}</span>
                </ListItem>
              );
            })}
          </List>
        </ListContainer>
      )}
    </Container>
  );
}
